import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useCourseStore from '../stores/useCourseStore';
import useProgressStore from '../stores/useProgressStore';
import { useToast } from '../components/ToastContainer';
import CryptoJS from 'crypto-js';
import axiosClient from '../services/api';

const QuizPage = () => {
  const { courseId, quizId } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    course,
    fetchCourse,
    isLoading: isCourseLoading,
    error: courseError
  } = useCourseStore();
  const {
    fetchProgress,
    updateQuizScore,
    isLoading: isProgressLoading,
    error: progressError
  } = useProgressStore();

  const [quiz, setQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const timerRef = useRef(null);
  const encryptionKey = process.env.REACT_APP_QUIZ_DRAFT_KEY;

  // Full-screen handler
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  // Fetch data
  useEffect(() => {
    if (courseId && quizId) {
      fetchCourse(courseId);
      fetchProgress(courseId);
    }
  }, [courseId, quizId, fetchCourse, fetchProgress]);

  // Find quiz in course
  useEffect(() => {
    if (course && quizId) {
      let foundQuiz = null;
      for (const module of course.modules || []) {
        for (const lesson of module.lessons || []) {
          if (lesson.quiz?._id === quizId) {
            foundQuiz = lesson.quiz;
            break;
          }
        }
        if (foundQuiz) break;
      }
      setQuiz(foundQuiz);
      if (foundQuiz) {
        setTimeLeft(foundQuiz.timeLimit * 60);
      }
    }
  }, [course, quizId]);

  // Load draft on mount
  useEffect(() => {
    if (quizId) {
      const encryptedDraft = localStorage.getItem(`quiz-draft-${quizId}`);
      if (encryptedDraft) {
        try {
          const bytes = CryptoJS.AES.decrypt(encryptedDraft, encryptionKey);
          const decrypted = bytes.toString(CryptoJS.enc.Utf8);
          if (decrypted) {
            setAnswers(JSON.parse(decrypted));
          }
        } catch (e) {
          console.warn('Failed to decrypt draft');
        }
      }
    }
  }, [quizId, encryptionKey]);

  // Save draft to localStorage (encrypted)
  useEffect(() => {
    if (quizId && hasStarted && !isSubmitted) {
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(answers), encryptionKey).toString();
      localStorage.setItem(`quiz-draft-${quizId}`, encrypted);
    }
  }, [answers, quizId, hasStarted, isSubmitted, encryptionKey]);

  // Timer
  useEffect(() => {
    if (hasStarted && timeLeft > 0 && !isSubmitted) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [hasStarted, timeLeft, isSubmitted]);

  const handleStartQuiz = () => {
    setHasStarted(true);
    setStartTime(new Date().toISOString());
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleSaveDraft = async () => {
    if (!quizId || !hasStarted) return;
    setIsSavingDraft(true);
    try {
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(answers), encryptionKey).toString();
      localStorage.setItem(`quiz-draft-${quizId}`, encrypted);
      toast.success('Draft saved successfully');
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setIsSavingDraft(false);
    }
  };


  const handleSubmit = async () => {
    if (!quiz || !courseId || !quizId) return;

    setIsSubmitted(true);
    clearInterval(timerRef.current);

    try {
      const response = await axiosClient.post(
        `/learning/quizzes/${quizId}/submit`,
        { answers, startTime, courseId }
      );

      const data = response.data;

      if (data.green) {
        setResults(data.data);

        await updateQuizScore(
          quizId,
          data.data.score,
          data.data.percentage,
          data.data.passed,
          courseId
        );

        // Clear quiz draft
        localStorage.removeItem(`quiz-draft-${quizId}`);
      } else {
        throw new Error(data.error || 'Failed to submit quiz');
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to submit quiz';
      toast.error(errorMsg);
      setIsSubmitted(false);
    }
  };


  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  if (isCourseLoading || isProgressLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (courseError || progressError) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Quiz not found</h2>
          <button
            onClick={() => navigate(-1)}
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  if (!hasStarted) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="text-primary-600 dark:text-primary-400 hover:underline mb-6 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to course
          </button>

          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 text-center">

            <div className='text-start mb-8'>
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">{quiz?.title}</h1>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">{quiz?.description}</p>
            </div>


            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Time Limit</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-white">{quiz?.timeLimit} minutes</p>
              </div>
              <div className="p-4 bg-secondary-50 dark:bg-secondary-900/20 rounded-lg">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Questions</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-white">{quiz?.questions?.length || 0}</p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Passing Score</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-white">{quiz?.passingScore}%</p>
              </div>
            </div>

            <button
              onClick={handleStartQuiz}
              className="px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white text-lg font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Start Quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted && results) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(`/student/learning/${courseId}`)}
            className="text-primary-600 dark:text-primary-400 hover:underline mb-6 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Course
          </button>

          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 text-center">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">Quiz Results</h1>
            <div className={`inline-block px-6 py-3 rounded-lg text-2xl font-bold mb-6 ${results?.passed
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              }`}>
              {results?.score} / {quiz?.totalPoints} ({results?.percentage.toFixed(1)}%)
            </div>
            <p className="text-xl text-neutral-600 dark:text-neutral-400 mb-8">
              {results?.passed ? '🎉 Congratulations! You passed!' : '❌ You did not pass. Try again!'}
            </p>

            <div className="space-y-6 max-h-[60vh] overflow-y-auto">
              {results?.results?.map((result, index) => (
                <div
                  key={index}
                  className={`bg-white dark:bg-neutral-800 rounded-lg shadow-sm border p-6 ${result?.isCorrect
                    ? 'border-green-200 dark:border-green-800'
                    : 'border-red-200 dark:border-red-800'
                    }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                      Question {index + 1}
                    </h3>
                    {result?.isCorrect ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        Correct
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                        Incorrect
                      </span>
                    )}
                  </div>
                  <p className="text-neutral-900 dark:text-white mb-3">{result?.question}</p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    <strong>Your Answer:</strong> {result?.userAnswer || 'No answer'}
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    <strong>Correct Answer:</strong> {result?.correctAnswer}
                  </p>
                  {result?.explanation && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
                      <strong>Explanation:</strong> {result?.explanation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to course
            </button>
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">{quiz.title}</h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-1">{quiz.description}</p>
          </div>
          <div className="flex flex-col items-end">
            <button
              onClick={toggleFullScreen}
              className="px-3 py-1 mb-2 bg-gray-700 text-white text-xs rounded"
            >
              {isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
            </button>
            <div className="bg-red-500 text-white px-4 py-2 rounded-lg font-medium">
              {formatTime(timeLeft)}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Progress</span>
            <span className="text-sm font-medium text-neutral-900 dark:text-white">
              {currentQuestionIndex + 1} of {quiz?.questions?.length}
            </span>
          </div>
          <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
            <div
              className="bg-primary-500 h-2 rounded-full"
              style={{ width: `${((currentQuestionIndex + 1) / quiz?.questions?.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Current Question */}
        {quiz?.questions && quiz?.questions[currentQuestionIndex] && (
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">
              Question {currentQuestionIndex + 1} <span className='text-xs text-primary-300'>(Points: {quiz.questions[currentQuestionIndex].points})</span>
            </h2>
            <p className="text-neutral-900 dark:text-white mb-6">
              {quiz.questions[currentQuestionIndex].question}
            </p>

            {quiz.questions[currentQuestionIndex].type === 'multiple-choice' && (
              <div className="space-y-3">
                {quiz.questions[currentQuestionIndex].options.map((option, idx) => (

                  <label
                    key={idx}
                    className="flex items-center p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                  >
                    <input
                      type="radio"
                      name={`question-${quiz.questions[currentQuestionIndex]._id}`}
                      checked={answers[quiz.questions[currentQuestionIndex]._id] === option.text}
                      onChange={() => handleAnswer(quiz.questions[currentQuestionIndex]._id, option.text)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-3 text-neutral-900 dark:text-white">{option.text}</span>
                  </label>
                ))}
              </div>
            )}

            {quiz.questions[currentQuestionIndex].type === 'true-false' && (
              <div className="flex space-x-6">
                {['True', 'False'].map((option) => (
                  <label
                    key={option}
                    className="flex items-center p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                  >
                    <input
                      type="radio"
                      name={`question-${quiz.questions[currentQuestionIndex]._id}`}
                      checked={answers[quiz.questions[currentQuestionIndex]._id] === option}
                      onChange={() => handleAnswer(quiz.questions[currentQuestionIndex]._id, option)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-3 text-neutral-900 dark:text-white">{option}</span>
                  </label>
                ))}
              </div>
            )}

            {quiz.questions[currentQuestionIndex].type === 'short-answer' && (
              <div>
                <textarea
                  value={answers[quiz.questions[currentQuestionIndex]._id] || ''}
                  onChange={(e) => handleAnswer(quiz.questions[currentQuestionIndex]._id, e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter your answer..."
                />
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
            disabled={currentQuestionIndex === 0}
            className="px-6 py-3 bg-neutral-200 hover:bg-neutral-300 disabled:bg-neutral-100 dark:bg-neutral-700 dark:hover:bg-neutral-600 dark:disabled:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
          >
            Previous
          </button>

          {currentQuestionIndex < (quiz.questions?.length || 1) - 1 ? (
            <button
              onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Next
            </button>
          ) : (
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft}
                className="px-6 py-3 bg-neutral-500 hover:bg-neutral-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
              >
                {isSavingDraft ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Submit Quiz
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizPage;